import "./metaDocColumns.scss";

import { commonColumnProps } from "constants/TableConstants";
import { useMemo } from "react";
import {
  buildMetadocPath,
  getMetadocNavigationContext,
} from "pages/metadoc/utils/metadocRoutes";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import type { Metadoc } from "services/metadoc-api";

import { formatFileSize } from "../DocumentConstants";
import { formatDate } from "../utils/formatters";
import { CloneMetadocButton } from "./CloneMetadocButton";
import EditableMetadocNameCell from "./EditableMetadocNameCell";

type MetaDocColumnsProps = {
  onDelete: (doc: Metadoc) => void;
  onToggleExpansion?: (metadocId: number) => void;
  fileId?: number;
  expandedIds?: Set<number>;
  isMetadocCloningEnabled?: boolean;
};

export const useMetaDocColumns = ({
  onDelete,
  onToggleExpansion,
  fileId,
  expandedIds = new Set(),
  isMetadocCloningEnabled = false,
}: MetaDocColumnsProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    fileId: routeFileId,
    documentManagementFileId,
    msaId,
    mcpId,
    lcpId,
    reviewId,
  } = useParams<{
    fileId?: string;
    documentManagementFileId?: string;
    msaId?: string;
    mcpId?: string;
    lcpId?: string;
    reviewId?: string;
  }>();
  const metadocNavigationContext = useMemo(
    () =>
      getMetadocNavigationContext(location.pathname, {
        fileId: routeFileId,
        documentManagementFileId,
        msaId,
        mcpId,
        lcpId,
        reviewId,
      }),
    [
      location.pathname,
      routeFileId,
      documentManagementFileId,
      msaId,
      mcpId,
      lcpId,
      reviewId,
    ],
  );

  const columns = useMemo(() => {
    const metadocColumns = [
      {
        key: "name",
        name: "Meta Document Name",
        ...commonColumnProps,
        minWidth: 400,
        renderCell: ({
          row,
        }: {
          row: Metadoc & { isChild?: boolean; parentMetadocId?: number };
        }) => {
          const isChild = row.isChild;
          const isExpanded = !isChild && expandedIds.has(row.id);
          const hasSourceDocs =
            !isChild && row.source_documents && row.source_documents.length > 0;

          return (
            <div
              className="metadoc-doc-name"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                paddingLeft: isChild ? "40px" : "0",
                width: "100%",
              }}
            >
              {!isChild && hasSourceDocs && onToggleExpansion && (
                <span
                  className="material-icons metadoc-doc-name__chevron"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleExpansion(row.id);
                  }}
                  style={{ cursor: "pointer", fontSize: "20px", flexShrink: 0 }}
                >
                  {isExpanded ? "expand_more" : "chevron_right"}
                </span>
              )}
              {!isChild && !hasSourceDocs && (
                <span style={{ width: "20px", flexShrink: 0 }} />
              )}
              <span
                className="material-icons metadoc-doc-name__icon metadoc-doc-name__icon--meta"
                style={{ fontSize: "20px", flexShrink: 0 }}
              >
                {isChild ? "description" : "layers"}
              </span>
              {isChild ? (
                <span className="metadoc-doc-name__text">{row.name}</span>
              ) : (
                <EditableMetadocNameCell
                  metadocId={row.id}
                  metadocName={row.name}
                  documentCount={
                    hasSourceDocs ? row.source_documents.length : undefined
                  }
                />
              )}
            </div>
          );
        },
      },
      {
        key: "actions",
        name: "Actions",
        ...commonColumnProps,
        width: "120px",
        sortable: false,
        renderCell: (props: {
          row: Metadoc & { isChild?: boolean; cloneInProgress?: boolean };
        }) => {
          const doc = props.row;
          const isChild = doc.isChild;

          // child rows (source documents) have no actions
          if (isChild) {
            return <div style={{ padding: "0 8px" }}>-</div>;
          }

          return (
            <div
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
              }}
            >
              {/* open in metadoc viewer */}
              {doc.status === "completed" && (
                <span
                  className="material-icons action-icon"
                  data-tooltip-id="metadoc-open-tooltip"
                  data-tooltip-content="Open Metadoc"
                  onClick={() => {
                    if (metadocNavigationContext) {
                      navigate(
                        buildMetadocPath(metadocNavigationContext, doc.id),
                      );
                    } else if (fileId) {
                      navigate(
                        buildMetadocPath(
                          {
                            workspace: "operations",
                            fileId,
                          },
                          doc.id,
                        ),
                      );
                    }
                  }}
                  style={{ cursor: "pointer" }}
                >
                  view_in_ar
                </span>
              )}

              {/* download metadoc - disabled for now */}
              {doc.status === "completed" && isMetadocCloningEnabled && (
                <CloneMetadocButton
                  metadoc={doc}
                  isCloneInProgressFromJobs={Boolean(doc.cloneInProgress)}
                />
              )}

              <span
                className="material-icons action-icon delete-icon"
                data-tooltip-id="metadoc-delete-tooltip"
                data-tooltip-content="Delete"
                onClick={() => onDelete(doc)}
                style={{ cursor: "pointer" }}
              >
                delete
              </span>
            </div>
          );
        },
      },
      {
        key: "status",
        name: "Status",
        ...commonColumnProps,
        width: "120px",
        renderCell: ({
          row,
        }: {
          row: Metadoc & { isChild?: boolean; cloneInProgress?: boolean };
        }) => {
          const isChild = row.isChild;

          if (isChild) {
            return <div>-</div>;
          }

          const statusMap: Record<string, string> = {
            pending: "Pending",
            processing: "Processing",
            completed: "Completed",
            failed: "Failed",
          };

          if (row.cloneInProgress) {
            return (
              <div className="metadoc-status">
                <span className="material-icons metadoc-status__spinner">
                  autorenew
                </span>
                <span>Processing</span>
              </div>
            );
          }

          return (
            <div>{row.status ? statusMap[row.status] || row.status : "-"}</div>
          );
        },
      },
      {
        key: "ai_run_status",
        name: "AI Run Status",
        ...commonColumnProps,
        width: "140px",
        sortable: false,
        renderCell: ({ row }: { row: Metadoc & { isChild?: boolean } }) => {
          if (row.isChild) {
            return <div>-</div>;
          }

          return (
            <div>{row.pre_ai_run_download_url ? "AI Run" : "Not Run"}</div>
          );
        },
      },
      {
        key: "page_count",
        name: "Pages",
        ...commonColumnProps,
        width: "100px",
        renderCell: ({ row }: { row: Metadoc }) => {
          return <div>{row.page_count || "-"}</div>;
        },
      },
      {
        key: "file_size",
        name: "File Size",
        ...commonColumnProps,
        width: "120px",
        sortable: false,
        renderCell: ({ row }: { row: Metadoc }) => {
          return <div>{formatFileSize(row.file_size_bytes) || "-"}</div>;
        },
      },
      {
        key: "created",
        name: "Created",
        ...commonColumnProps,
        width: "180px",
        renderCell: ({ row }: { row: Metadoc & { isChild?: boolean } }) => {
          const isChild = row.isChild;

          if (isChild) {
            return <div>{formatDate(row.created)}</div>;
          }

          return <div>{formatDate(row.created)}</div>;
        },
      },
    ];

    return metadocColumns;
  }, [
    onDelete,
    onToggleExpansion,
    fileId,
    navigate,
    expandedIds,
    metadocNavigationContext,
  ]);

  return columns;
};
